/**
 * Org-host routing helpers.
 *
 * Organisation dashboards normally live on <subdomain>.<rootDomain>. That does
 * not work on localhost or Vercel preview deployments (no wildcard DNS), so
 * those environments use path-based routing: /subdomain/<org>/...
 *
 * This file is the single source of truth for that choice. Import from here —
 * do not inline `.vercel.app` / localhost checks elsewhere.
 *
 * Two preview detectors:
 *  - isVercelPreviewHostname()  — server-side (pure function, no browser globals)
 *  - isVercelPreviewDeploy()    — client-side (reads window.location, call only in browser)
 *
 * The server-side helpers take the root domain as the last argument. It
 * defaults to the configured one, so tests can pass their own.
 */

import type { UrlOrigin } from "./navigation-utils";

const MAIN_DOMAIN =
  process.env.NEXT_PUBLIC_ROOT_DOMAIN || process.env.ROOT_DOMAIN;

/**
 * Returns true for a Vercel preview hostname that is NOT a subdomain of the
 * configured root domain.
 *
 * Safe to call from middleware and React Server Components.
 *
 * @example
 * isVercelPreviewHostname("detour-website-abc-software-mansion.vercel.app") // true
 * isVercelPreviewHostname("myorg.godetour.app")                              // false
 */
export function isVercelPreviewHostname(
  hostname: string,
  rootDomain = MAIN_DOMAIN
): boolean {
  if (!rootDomain) return false;
  return (
    hostname.endsWith(".vercel.app") && !hostname.endsWith(`.${rootDomain}`)
  );
}

export function hostnameFromHostHeader(hostHeader: string | null) {
  if (!hostHeader) return "localhost";
  return hostHeader.split(":")[0];
}

function deriveMainDomain(hostname: string, rootDomain = MAIN_DOMAIN) {
  if (rootDomain) {
    if (isVercelPreviewHostname(hostname, rootDomain)) {
      return hostname;
    }
    return rootDomain;
  }
  const parts = hostname.split(".");
  if (parts.length <= 2) return hostname; // already apex (localhost or example.com)
  return parts.slice(-2).join(".");
}

export type RequestHost = {
  hostname: string;
  protocol: string;
  mainDomain: string;
  mainDomainWithPort: string;
};

function originOfRequest(request: { headers: Headers }): UrlOrigin {
  return {
    host: request.headers.get("host") || "localhost:3000",
    // `next dev --experimental-https` sets no x-forwarded-proto, so default to
    // https.
    protocol: request.headers.get("x-forwarded-proto") || "https",
  };
}

function hostOf(origin: UrlOrigin, rootDomain: string | undefined): RequestHost {
  const hostname = hostnameFromHostHeader(origin.host);
  const port = origin.host.split(":")[1];
  const mainDomain = deriveMainDomain(hostname, rootDomain);

  return {
    hostname,
    // `window.location.protocol` ends in a colon; x-forwarded-proto doesn't.
    protocol: origin.protocol.replace(/:$/, ""),
    mainDomain,
    mainDomainWithPort: port ? `${mainDomain}:${port}` : mainDomain,
  };
}

export function requestHost(
  request: { headers: Headers },
  rootDomain = MAIN_DOMAIN
): RequestHost {
  return hostOf(originOfRequest(request), rootDomain);
}

/**
 * The URL of an Organization's dashboard root:
 *  - Localhost / Vercel preview  → path-based:  https://<host>/subdomain/<org>/
 *  - Staging / prod              → subdomain:   https://<org>.<rootDomain>/
 *
 * It always points to `orgSubdomain`, even when called from another
 * Organization's host.
 */
export function orgRootUrl(
  from: { headers: Headers } | UrlOrigin,
  orgSubdomain: string,
  rootDomain = MAIN_DOMAIN
): string {
  const origin = "headers" in from ? originOfRequest(from) : from;
  const { hostname, protocol, mainDomain, mainDomainWithPort } = hostOf(
    origin,
    rootDomain
  );
  // In dev the session cookie is host-only for `localhost`, so `<org>.localhost`
  // reads as signed out and redirects back here forever.
  const isLocalhost =
    mainDomainWithPort.includes("localhost") || hostname.includes("localhost");

  if (!isVercelPreviewHostname(hostname, rootDomain) && !isLocalhost) {
    return `${protocol}://${orgSubdomain}.${mainDomainWithPort}/`;
  }
  // Keep a host outside the root domain, like 127.0.0.1. Moving to localhost
  // would lose the session cookie.
  const host = hostname.endsWith(`.${mainDomain}`)
    ? mainDomainWithPort
    : origin.host;
  return `${protocol}://${host}/subdomain/${orgSubdomain}/`;
}

/**
 * Returns true when the browser is on a Vercel preview deployment that is NOT
 * a subdomain of the configured ROOT_DOMAIN.
 *
 * Safe to call only from Client Components and hooks (requires `window`).
 *
 * @example
 * // inside a hook / client component:
 * if (isVercelPreviewDeploy()) { /* use path-based org routing *‌/ }
 */
export function isVercelPreviewDeploy(): boolean {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  const rootDomainEnv = process.env.NEXT_PUBLIC_ROOT_DOMAIN || "";
  return (
    hostname.endsWith(".vercel.app") && !hostname.endsWith(`.${rootDomainEnv}`)
  );
}
